import { Link, useLocation } from "react-router-dom";
import { Calculator, BookOpen, Mail } from "lucide-react";

// 2026-08-30 — Mobile-only sticky bottom nav (kept off desktop with `md:hidden`).
// Three most-common tasks a homeowner comes here to do: get an estimate,
// read a guide, ask a question. Bar is intentionally minimal — 56px tall,
// safe-area-inset aware for iPhone home indicator, active-state highlights
// so users know where they are without opening the hamburger.
//
// Design decision: "Estimate" links to /calculators (state-adjusted via
// hcg_state localStorage) rather than opening the LeadDialog. The dialog
// is the "I want quotes" path; the bar is the "I want a number" path.
// Both funnels stay clearly available (dialog via Header CTA, bar via
// /calculators).
const ITEMS = [
  {
    to: "/calculators",
    label: "Estimate",
    Icon: Calculator,
    testid: "mobile-bottom-bar-estimate",
    matchPrefixes: ["/calculators", "-cost-calculator"],
  },
  {
    to: "/guides",
    label: "Guides",
    Icon: BookOpen,
    testid: "mobile-bottom-bar-guides",
    matchPrefixes: ["/guides"],
  },
  {
    to: "/contact",
    label: "Contact",
    Icon: Mail,
    testid: "mobile-bottom-bar-contact",
    matchPrefixes: ["/contact", "/about"],
  },
];

function isActive(pathname, prefixes) {
  return prefixes.some((p) =>
    p.startsWith("/") ? pathname === p || pathname.startsWith(`${p}/`) : pathname.includes(p)
  );
}

export default function MobileBottomBar() {
  const { pathname } = useLocation();
  // Hide on embed / chromeless routes — the Layout parent already skips
  // rendering us there, but guard again in case this ever gets mounted
  // outside Layout.
  if (pathname.startsWith("/embed/") || pathname.startsWith("/admin")) return null;

  return (
    <nav
      aria-label="Quick navigation"
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-lg pb-[env(safe-area-inset-bottom)] md:hidden"
      data-testid="mobile-bottom-bar"
    >
      <ul className="mx-auto grid max-w-3xl grid-cols-3">
        {ITEMS.map(({ to, label, Icon, testid, matchPrefixes }) => {
          const active = isActive(pathname, matchPrefixes);
          return (
            <li key={to}>
              <Link
                to={to}
                className={`flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition ${
                  active
                    ? "text-emerald-700"
                    : "text-slate-600 hover:text-slate-900 active:text-emerald-700"
                }`}
                data-testid={testid}
                aria-current={active ? "page" : undefined}
              >
                <Icon
                  className={`h-5 w-5 ${active ? "text-emerald-700" : "text-slate-500"}`}
                  strokeWidth={active ? 2.4 : 1.9}
                  aria-hidden
                />
                <span>{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
